import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { Play, Pause, RotateCcw, Lock, Unlock, X, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

// --- Unit Conversion Constants ---
// Worker output is in standard units: IRI in in/mi, elevation in inches.
// These factors only kick in when the user toggles to metric display.
const IN_PER_MI_TO_M_PER_KM = 1 / 63.3596;   // in/mi → m/km
const INCHES_TO_METERS = 0.0254;             // in → m
const DEFAULT_WAVELENGTH_FT = 300;

// --- Utility Functions ---
const getVisibleYStats = (traces, xStart, xEnd) => {
  const safeStart = Math.min(xStart, xEnd);
  const safeEnd = Math.max(xStart, xEnd);

  let min = Infinity;
  let max = -Infinity;
  let hasData = false;

  traces.forEach((trace) => {
    if (!trace || !trace.x || !trace.y) return;
    const len = trace.x.length;
    for (let i = 0; i < len; i++) {
      const xVal = trace.x[i];
      // Skip gaps
      if (xVal === null || xVal === undefined) continue;
      
      // Check bounds
      if (xVal >= safeStart && xVal <= safeEnd) {
        const yVal = trace.y[i];
        if (yVal !== null && yVal !== undefined && !isNaN(yVal) && isFinite(yVal)) {
           if (yVal < min) min = yVal;
           if (yVal > max) max = yVal;
           hasData = true;
        }
      }
    }
  });
  return hasData ? { min, max } : null;
};

const findClosestIndex = (profile, targetStn) => {
  if (!profile || profile.length === 0) return -1;
  const isObjectArray = profile[0] && typeof profile[0] === 'object' && 'station' in profile[0];
  const getVal = (i) => isObjectArray ? profile[i].station : profile[i];

  let left = 0;
  let right = profile.length - 1;
  
  // Handle Descending Data (Reverse Stationing stored in order)
  const isDescending = getVal(0) > getVal(right);
  
  if (isDescending) {
      if (targetStn >= getVal(0)) return 0;
      if (targetStn <= getVal(right)) return right;
  } else {
      if (targetStn <= getVal(0)) return 0;
      if (targetStn >= getVal(right)) return right;
  }
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const val = getVal(mid);
    if (val === targetStn) return mid;
    
    if (isDescending) {
        if (val > targetStn) left = mid + 1;
        else right = mid - 1;
    } else {
        if (val < targetStn) left = mid + 1;
        else right = mid - 1;
    }
  }
  
  const pLeft = getVal(right); 
  const pRight = getVal(left); 
  if (pLeft === undefined) return left;
  if (pRight === undefined) return right;
  return (Math.abs(targetStn - pLeft) < Math.abs(targetStn - pRight)) ? right : left;
};

const prepareDataWithGaps = (data, valueKey, factor, ignoredSegments) => {
  if (!data || data.length === 0) return { x: [], y: [] };
  const x = [];
  const y = [];
  let wasIgnored = false;
  const hasIgnored = ignoredSegments && ignoredSegments.length > 0;
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    let isIgnored = false;
    if (hasIgnored) isIgnored = ignoredSegments.some(seg => d.station >= seg.from && d.station <= seg.to);
    if (isIgnored) {
      wasIgnored = true;
      continue;
    }
    if (wasIgnored && x.length > 0) {
      x.push(null);
      y.push(null);
    }
    x.push(d.station);
    y.push(d[valueKey] * factor);
    wasIgnored = false;
  }
  return { x, y };
};

// --- Smoothing Helpers ---
const applyHighPassFilter = (profile, wavelengthFt) => {
  if (!profile || profile.length === 0) return [];
  const prefixSum = new Float64Array(profile.length);
  let currentSum = 0;
  for (let i = 0; i < profile.length; i++) {
    currentSum += profile[i].elevation;
    prefixSum[i] = currentSum;
  }
  const result = [];
  const halfWindow = wavelengthFt / 2;
  const n = profile.length;
  let leftIdx = 0;
  let rightIdx = 0;
  
  for (let i = 0; i < n; i++) {
    const currentStation = profile[i].station;
    const windowStart = currentStation - halfWindow;
    const windowEnd = currentStation + halfWindow;

    while (leftIdx < n - 1 && Math.abs(profile[leftIdx].station - currentStation) > halfWindow && (i > leftIdx)) {
        leftIdx++;
    }
    rightIdx = Math.max(rightIdx, i);
    while (rightIdx < n - 1 && Math.abs(profile[rightIdx + 1].station - currentStation) <= halfWindow) {
        rightIdx++;
    }

    const count = rightIdx - leftIdx + 1;
    if (count > 0) {
      const sumRight = prefixSum[rightIdx];
      const sumLeft = leftIdx > 0 ? prefixSum[leftIdx - 1] : 0;
      const average = (sumRight - sumLeft) / count;
      result.push({ ...profile[i], texture: profile[i].elevation - average });
    } else {
      result.push({ ...profile[i], texture: 0 });
    }
  }
  return result;
};

const applyMovingAverage = (data, key, windowSize) => {
  if (!data || data.length === 0) return [];
  const result = [];
  const half = Math.floor(windowSize / 2);
  const len = data.length;

  for (let i = 0; i < len; i++) {
    let sum = 0;
    let count = 0;
    for (let k = -half; k <= half; k++) {
      const idx = i + k;
      if (idx >= 0 && idx < len) {
        const val = data[idx][key];
        if (val !== null && val !== undefined && typeof val === 'number') {
            sum += val;
            count++;
        }
      }
    }
    const avg = count > 0 ? sum / count : (data[i][key] || 0);
    result.push({ ...data[i], [key]: avg });
  }
  return result;
};

function KmlChartsDrawer({
  iriProfileData,
  elevationProfileData,
  stationRange, 
  currentStation,
  setCurrentStation,
  isPlaying,
  setIsPlaying,
  playbackSpeed,
  setPlaybackSpeed,
  isCameraLocked,
  setIsCameraLocked,
  onClose,
  units = 'standard',
  thresholds = {},
  alrDefects = [],
  surfaceEvents = [], 
  ignoredSegments = [],
  targetZoomRange,
  onSelectAlr,
  segments = [],
  showSegments = false,
  selectedSegment = null,
  hoveredStation = null,
  sessionData, 
  // Prop passed from parent
  isReversed = false,
  onChartHover = () => {}
}) {
  const [localValue, setLocalValue] = useState(currentStation);
  const isDraggingRef = useRef(false); 
  const throttleTimeout = useRef(null);
  const THROTTLE_DELAY_MS = 50;
  const hoverThrottleRef = useRef(null);
  const hoverLineRef = useRef(null);

  // Logical Start/End based on direction
  const runStart = isReversed ? stationRange.max : stationRange.min;
  const runEnd = isReversed ? stationRange.min : stationRange.max;

  const [wheelpathVisibility, setWheelpathVisibility] = useState({
    left: true,
    right: true,
    avg: false
  });

  const [activeChart, setActiveChart] = useState('iri');
  
  // Initialize Shared Range based on Direction
  // If Reversed, Start=Max, End=Min. Plotly [1000, 0] draws X-axis flipped.
  const [sharedXRange, setSharedXRange] = useState([runStart, runEnd]);

  const cutoffWavelength = useMemo(() => {
    return parseFloat(sessionData?.meta?.['high-pass_filter(ft.)']) || DEFAULT_WAVELENGTH_FT;
  }, [sessionData]);

  useEffect(() => {
    if (targetZoomRange && targetZoomRange.length === 2) {
        // If parent passes a specific range (e.g., segment click), use it.
        // Ensure it respects direction:
        // If reversed, range should be [High, Low]. If normal, [Low, High].
        const min = Math.min(targetZoomRange[0], targetZoomRange[1]);
        const max = Math.max(targetZoomRange[0], targetZoomRange[1]);
        setSharedXRange(isReversed ? [max, min] : [min, max]);
    } else {
        // Reset to full run
        setSharedXRange([runStart, runEnd]);
    }
  }, [targetZoomRange, runStart, runEnd, isReversed]);

  const formatStation = useCallback((distance) => {
    if (distance === null || distance === undefined) return "0+00";
    const hundreds = Math.floor(distance / 100);
    const remainder = distance % 100;
    return `${hundreds}+${remainder.toFixed(0).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalValue(currentStation);
    }
  }, [currentStation]);

  const throttledSetCurrentStation = useCallback((value) => {
    if (!throttleTimeout.current) {
      setCurrentStation(value);
      throttleTimeout.current = setTimeout(() => {
        throttleTimeout.current = null;
      }, THROTTLE_DELAY_MS);
    }
  }, [setCurrentStation]);

  const snapViewToStation = useCallback((station) => {
    if (!isCameraLocked) return; 
    setSharedXRange(prevRange => {
        // Calculate absolute window width
        const currentSpan = Math.abs(prevRange[1] - prevRange[0]);
        
        // Center the view on the current station
        let newStart, newEnd;
        
        if (isReversed) {
           // Range is Descending [High, Low]
           // Station is e.g. 900. Span is 100.
           // Start (Left) should be 900 + 10 = 910
           // End (Right) should be 900 - 90 = 810
           newStart = station + (currentSpan * 0.1); 
           newEnd = newStart - currentSpan;
        } else {
           // Range is Ascending [Low, High]
           newStart = station - (currentSpan * 0.1); 
           newEnd = newStart + currentSpan;
        }
        
        return [newStart, newEnd];
    });
  }, [isCameraLocked, isReversed]);

  const handleScrub = (e) => {
    const rawValue = Number(e.target.value);
    
    // Scrubber logic:
    // If Normal: Input is [Min, Max]. Val = Station.
    // If Reversed: Input is [Min, Max]. 
    //   Left (Min value) should visually map to Start (Max Station).
    //   Right (Max value) should visually map to End (Min Station).
    //   So we invert the mapping.
    
    const actualStation = isReversed 
      ? (stationRange.max - rawValue + stationRange.min)
      : rawValue;

    setLocalValue(actualStation);
    snapViewToStation(actualStation);
    throttledSetCurrentStation(actualStation);
  };

  const handleChartClick = useCallback((data) => {
    if (data && data.points && data.points[0]) {
        const clickedStation = data.points[0].x;
        setCurrentStation(clickedStation);
        setLocalValue(clickedStation);
        snapViewToStation(clickedStation);
    }
  }, [setCurrentStation, snapViewToStation]);

  const hoverStaRef = useRef(null);
  const hoverLeftRef = useRef(null);
  const hoverRightRef = useRef(null);
  const hoverAvgRef = useRef(null);

  // Pre-slice profile data to visible range for fast hover lookups
  const visibleProfileSlice = useRef({ left: [], right: [], avg: [] });

  useEffect(() => {
    if (!iriProfileData) return;
    const { leftWheelpath, rightWheelpath, mergedData } = iriProfileData;
    const lo = Math.min(sharedXRange[0], sharedXRange[1]);
    const hi = Math.max(sharedXRange[0], sharedXRange[1]);
    const pad = (hi - lo) * 0.05; // small buffer
    const inRange = (arr) => {
      if (!arr || arr.length === 0) return [];
      const startIdx = findClosestIndex(arr, lo - pad);
      const endIdx = findClosestIndex(arr, hi + pad);
      const from = Math.min(startIdx, endIdx);
      const to = Math.max(startIdx, endIdx);
      return arr.slice(Math.max(0, from), to + 1);
    };
    visibleProfileSlice.current = {
      left: inRange(leftWheelpath),
      right: inRange(rightWheelpath),
      avg: inRange(mergedData),
    };
  }, [iriProfileData, sharedXRange]);

  const handleChartHoverWithPixel = useCallback((data) => {
    if (!data || !data.points || data.points.length === 0) {
      if (hoverStaRef.current) hoverStaRef.current.textContent = '—';
      if (hoverLeftRef.current) hoverLeftRef.current.textContent = '—';
      if (hoverRightRef.current) hoverRightRef.current.textContent = '—';
      if (hoverAvgRef.current) hoverAvgRef.current.textContent = '—';
      onChartHover(null);
      return;
    }

    const station = data.points[0].x;

    if (hoverStaRef.current) hoverStaRef.current.textContent = formatStation(station);

    // Fast lookup on pre-sliced visible data
    const { left, right, avg } = visibleProfileSlice.current;
    const leftIdx = findClosestIndex(left, station);
    const rightIdx = findClosestIndex(right, station);
    const avgIdx = findClosestIndex(avg, station);
    if (hoverLeftRef.current) hoverLeftRef.current.textContent = leftIdx >= 0 ? left[leftIdx].iri.toFixed(1) : '—';
    if (hoverRightRef.current) hoverRightRef.current.textContent = rightIdx >= 0 ? right[rightIdx].iri.toFixed(1) : '—';
    if (hoverAvgRef.current) hoverAvgRef.current.textContent = avgIdx >= 0 ? avg[avgIdx].avgIRI.toFixed(1) : '—';

    // Throttle the callback to FusionMap (ghost marker updates)
    if (!hoverThrottleRef.current) {
      onChartHover(station);
      hoverThrottleRef.current = setTimeout(() => { hoverThrottleRef.current = null; }, 50);
    }
  }, [onChartHover, formatStation]);

  // --- PLAYBACK LOGIC ---
  useEffect(() => {
    if (!isPlaying) return;

    // Stop Condition
    const isFinished = isReversed 
        ? localValue <= stationRange.min 
        : localValue >= stationRange.max;

    if (isFinished) {
      setIsPlaying(false);
      return;
    }
    
    const speeds = { 0.5: 100, 1: 50, 2: 25, 4: 10, 8: 5 };
    const delay = speeds[playbackSpeed] || 50;
    
    // Step: +10 for Normal, -10 for Reverse
    const step = isReversed ? -10 : 10;
    
    const timerId = setTimeout(() => {
      let nextVal = localValue + step;
      
      // Clamp
      if (isReversed) nextVal = Math.max(nextVal, stationRange.min);
      else nextVal = Math.min(nextVal, stationRange.max);

      setLocalValue(nextVal);
      setCurrentStation(nextVal);

      if (isCameraLocked) {
        // Shift the window by 'step' to follow playback
        setSharedXRange(prev => [prev[0] + step, prev[1] + step]);
      }
    }, delay);
    return () => clearTimeout(timerId);
  }, [isPlaying, localValue, playbackSpeed, stationRange, setCurrentStation, setIsPlaying, isCameraLocked, isReversed]);

  const handleReset = () => {
    setCurrentStation(runStart);
    setIsPlaying(false);
    setSharedXRange([runStart, runEnd]);
  };

  const speedOptions = [0.5, 1, 2, 4, 8];
  
  // Progress % for Gradient Bar
  const totalDist = stationRange.max - stationRange.min;
  const progressPercent = totalDist > 0 
    ? (isReversed 
        ? ((stationRange.max - localValue) / totalDist) * 100
        : ((localValue - stationRange.min) / totalDist) * 100)
    : 0;

  // Input Value Mapping
  // Normal: Value = Station
  // Reverse: Value needs to go Min->Max as we scrub Left->Right.
  //   Left (Min Input) = Start (Max Station). 
  //   Right (Max Input) = End (Min Station).
  //   Val = (MaxStation - CurrentStation) + MinStation
  const inputValue = isReversed 
    ? (stationRange.max - localValue + stationRange.min)
    : localValue;

  const toggleWheelpath = (path) => {
    setWheelpathVisibility(prev => ({ ...prev, [path]: !prev[path] }));
  };

  return (
   <div className="relative w-full h-full bg-white text-gray-900 border-t border-gray-200 shadow-2xl flex flex-col">
      {/* Header bar with live stats */}
      <div className="flex-shrink-0 px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Station */}
          <span ref={hoverStaRef} className="text-xs font-semibold text-gray-700 font-mono tabular-nums w-20">
            {formatStation(localValue)}
          </span>

          <div className="w-px h-4 bg-gray-300" />

          {/* Live IRI values */}
          <div className="flex items-center gap-3 text-[11px] font-mono tabular-nums">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className="text-gray-500">L</span>
              <span ref={hoverLeftRef} className="font-semibold text-gray-700 w-10">—</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span className="text-gray-500">R</span>
              <span ref={hoverRightRef} className="font-semibold text-gray-700 w-10">—</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
              <span className="text-gray-500">MRI</span>
              <span ref={hoverAvgRef} className="font-semibold text-gray-700 w-10">—</span>
            </span>
          </div>

          <div className="w-px h-4 bg-gray-300" />

          {/* Wheelpath toggles */}
          <div className="flex items-center gap-0.5">
            <button onClick={() => toggleWheelpath('left')} className={`px-2 py-1 text-[11px] font-semibold rounded transition-colors ${wheelpathVisibility.left ? 'bg-green-100 text-green-800' : 'text-gray-500 hover:bg-gray-100'}`}>L</button>
            <button onClick={() => toggleWheelpath('right')} className={`px-2 py-1 text-[11px] font-semibold rounded transition-colors ${wheelpathVisibility.right ? 'bg-blue-100 text-blue-800' : 'text-gray-500 hover:bg-gray-100'}`}>R</button>
            <button onClick={() => toggleWheelpath('avg')} className={`px-2 py-1 text-[11px] font-semibold rounded transition-colors ${wheelpathVisibility.avg ? 'bg-purple-100 text-purple-800' : 'text-gray-500 hover:bg-gray-100'}`}>AVG</button>
          </div>

          <div className="w-px h-4 bg-gray-300" />

          {/* Camera lock */}
          <button onClick={() => setIsCameraLocked(!isCameraLocked)} className={`p-1 rounded transition-colors ${isCameraLocked ? 'bg-red-100 text-red-700' : 'text-gray-500 hover:bg-gray-100'}`} title={isCameraLocked ? 'Camera locked' : 'Camera unlocked'}>
            {isCameraLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </button>

        </div>

        {/* Pan / Reset / Close */}
        <div className="flex items-center rounded-md overflow-hidden border border-gray-300">
          <button
            onClick={() => setSharedXRange(prev => {
              const span = Math.abs(prev[1] - prev[0]);
              const step = span * 0.3 * (isReversed ? 1 : -1);
              return [prev[0] + step, prev[1] + step];
            })}
            className="p-1.5 text-gray-500 hover:bg-gray-100 transition-colors" title="Pan left"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-4 bg-gray-300" />
          <button onClick={() => setSharedXRange([runStart, runEnd])} className="p-1.5 text-gray-500 hover:bg-gray-100 transition-colors" title="Reset zoom">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-4 bg-gray-300" />
          <button
            onClick={() => setSharedXRange(prev => {
              const span = Math.abs(prev[1] - prev[0]);
              const step = span * 0.3 * (isReversed ? -1 : 1);
              return [prev[0] + step, prev[1] + step];
            })}
            className="p-1.5 text-gray-500 hover:bg-gray-100 transition-colors" title="Pan right"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-4 bg-gray-300" />
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:bg-gray-100 transition-colors" title="Close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Stacked charts */}
      <div
        className="flex-1 flex flex-col overflow-hidden relative"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          if (hoverLineRef.current) {
            hoverLineRef.current.style.left = `${x}px`;
            hoverLineRef.current.style.opacity = '1';
          }
        }}
        onMouseLeave={() => {
          if (hoverLineRef.current) hoverLineRef.current.style.opacity = '0';
        }}
      >
        {/* Hover crosshair line */}
        <div
          ref={hoverLineRef}
          className="absolute top-0 bottom-0 w-px bg-blue-400/50 pointer-events-none z-10 transition-opacity duration-75"
          style={{ opacity: 0 }}
        />

        {/* IRI Chart */}
        {iriProfileData && (
          <div className="flex-1 min-h-0">
            <FullIRIChart
              iriProfileData={iriProfileData}
              currentStation={localValue}
              stationRange={stationRange}
              units={units}
              thresholds={thresholds}
              wheelpathVisibility={wheelpathVisibility}
              alrDefects={alrDefects}
              ignoredSegments={ignoredSegments}
              formatStation={formatStation}
              xAxisRange={sharedXRange}
              setXAxisRange={setSharedXRange}
              onSelectAlr={onSelectAlr}
              segments={segments}
              showSegments={showSegments}
              selectedSegment={selectedSegment}
              hoveredStation={hoveredStation}
              onChartHoverRaw={handleChartHoverWithPixel}
              onChartClick={handleChartClick}
              isReversed={isReversed}
            />
          </div>
        )}

        {/* Elevation Chart */}
        {elevationProfileData && (
          <div className="flex-1 min-h-0">
            <FullElevationChart
              elevationProfileData={elevationProfileData}
              currentStation={localValue}
              stationRange={stationRange}
              units={units}
              wheelpathVisibility={wheelpathVisibility}
              ignoredSegments={ignoredSegments}
              surfaceEvents={surfaceEvents}
              formatStation={formatStation}
              xAxisRange={sharedXRange}
              setXAxisRange={setSharedXRange}
              segments={segments}
              showSegments={showSegments}
              selectedSegment={selectedSegment}
              hoveredStation={hoveredStation}
              onChartHoverRaw={handleChartHoverWithPixel}
              onChartClick={handleChartClick}
              cutoffWavelength={cutoffWavelength}
              isReversed={isReversed}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Optimized IRI Chart
const FullIRIChart = React.memo(({ 
  iriProfileData, 
  currentStation, 
  stationRange, 
  units, 
  thresholds,
  wheelpathVisibility,
  ignoredSegments,
  formatStation,
  xAxisRange, 
  setXAxisRange,
  alrDefects,
  onSelectAlr,
  segments, 
  showSegments,
  selectedSegment,
  hoveredStation,
  onChartHoverRaw,
  onChartClick,
  isReversed // Received Prop
}) => {
  
  const [contextMenu, setContextMenu] = useState(null);
  const hoveredPointRef = useRef(null);

  useEffect(() => {
      const closeMenu = () => setContextMenu(null);
      window.addEventListener('click', closeMenu);
      return () => window.removeEventListener('click', closeMenu);
  }, []);

  const isStandard = units === 'standard';
  const iriFactor = isStandard ? 1.0 : (IN_PER_MI_TO_M_PER_KM);
  const iriUnit = isStandard ? 'in/mi' : 'm/km';

  const { leftTrace, rightTrace, avgTrace } = useMemo(() => {
    const { leftWheelpath, rightWheelpath, mergedData } = iriProfileData;
    return {
      leftTrace: prepareDataWithGaps(leftWheelpath, 'iri', iriFactor, ignoredSegments),
      rightTrace: prepareDataWithGaps(rightWheelpath, 'iri', iriFactor, ignoredSegments),
      avgTrace: prepareDataWithGaps(mergedData, 'avgIRI', iriFactor, ignoredSegments)
    };
  }, [iriProfileData, iriFactor, ignoredSegments]);

  // ... (alrTraces logic unchanged, filtering logic is range-agnostic) ...
  const { alrTraces } = useMemo(() => {
    if (!alrDefects || alrDefects.length === 0) return { alrTraces: [] };
    const traces = [];
    const thresholdVal = (thresholds && thresholds.active) ? (isStandard ? thresholds.active : thresholds.active * IN_PER_MI_TO_M_PER_KM) : 0;
    
    const processWheelpath = (wpKey, dataTrace, colorRgba, colorHex) => {
        if (!wheelpathVisibility[wpKey]) return;
        const wpDefects = alrDefects.filter(d => d.wheelpath === (wpKey === 'left' ? 'Left' : 'Right'));
        wpDefects.forEach(alr => {
            const start = alr.startDistance || alr.fromStation;
            const end = alr.stopDistance || alr.toStation;
            if (start === undefined || end === undefined) return;
            const xData = dataTrace.x;
            const yData = dataTrace.y;
            const pointsX = [];
            const pointsY = [];
            
            for(let i = 0; i < xData.length; i++) {
                if (xData[i] !== null &&
                    ((start <= end && xData[i] >= start && xData[i] <= end) ||
                     (start > end && xData[i] <= start && xData[i] >= end))) {
                    pointsX.push(xData[i]);
                    pointsY.push(yData[i]);
                }
            }
            if (pointsX.length > 0) {
                traces.push({
                    x: [pointsX[0], ...pointsX, pointsX[pointsX.length-1]],
                    y: [thresholdVal, ...pointsY, thresholdVal], 
                    fill: 'toself',
                    fillcolor: colorRgba,
                    line: { width: 0 },
                    mode: 'lines', 
                    type: 'scatter', 
                    hoverinfo: 'text',
                    text: `ALR: ${formatStation(start)} - ${formatStation(end)}`,
                    hoveron: 'fills', 
                    showlegend: false,
                    meta: { isAlr: true, id: alr.eventId } 
                });
                const maxY = Math.max(...pointsY);
                traces.push({
                    x: [start],
                    y: [maxY * 1.02], 
                    mode: 'text',
                    text: [`ALR @ ${formatStation(start)}`],
                    textposition: 'top right',
                    textfont: { family: 'sans-serif', size: 10, color: colorHex },
                    type: 'scatter', 
                    hoverinfo: 'skip',
                    showlegend: false,
                    meta: { isAlr: true, id: alr.eventId }
                });
            }
        });
    };

    if (wheelpathVisibility.left) processWheelpath('left', leftTrace, 'rgba(16, 185, 129, 0.25)', '#10b981');
    if (wheelpathVisibility.right) processWheelpath('right', rightTrace, 'rgba(59, 130, 246, 0.25)', '#3b82f6');

    return { alrTraces: traces };
  }, [alrDefects, wheelpathVisibility, leftTrace, rightTrace, formatStation, thresholds, isStandard]);

  const yAxisRange = useMemo(() => {
    const arraysToCheck = [];
    if (wheelpathVisibility.left) arraysToCheck.push(leftTrace);
    if (wheelpathVisibility.right) arraysToCheck.push(rightTrace);
    if (wheelpathVisibility.avg) arraysToCheck.push(avgTrace);

    const currentXMin = xAxisRange ? xAxisRange[0] : stationRange.min;
    const currentXMax = xAxisRange ? xAxisRange[1] : stationRange.max;

    // Updated getVisibleYStats handles reverse ranges (Start > End)
    const stats = getVisibleYStats(arraysToCheck, currentXMin, currentXMax);
    
    if (!stats) return [0, 200]; 
    const { min, max } = stats;
    const finalMax = Math.max(max, isStandard ? thresholds?.active : (thresholds?.active * IN_PER_MI_TO_M_PER_KM) || 0);
    const range = finalMax - min;
    const padding = range === 0 ? 10 : range * 0.1;
    return [Math.max(0, min - padding), finalMax + padding];
  }, [leftTrace, rightTrace, avgTrace, wheelpathVisibility, xAxisRange, thresholds, isStandard, stationRange]);

  const plotData = useMemo(() => {
    const data = [];
    if (alrTraces) data.push(...alrTraces);
    const getStationLabels = (xArr) => xArr.map(x => x !== null ? formatStation(x) : '');
    const commonLayout = { type: 'scatter', mode: 'lines', hoverinfo: 'y+text' };

    if (wheelpathVisibility.left && leftTrace.x.length > 0) {
      data.push({
        ...commonLayout,
        x: leftTrace.x, y: leftTrace.y,
        name: 'Left Wheelpath', line: { color: '#10b981', width: 2 },
        customdata: getStationLabels(leftTrace.x), 
        hovertemplate: `<b>Sta:</b> %{customdata}<br><b>IRI:</b> %{y:.1f} ${iriUnit}<extra></extra>`
      });
    }
    if (wheelpathVisibility.right && rightTrace.x.length > 0) {
      data.push({
        ...commonLayout,
        x: rightTrace.x, y: rightTrace.y,
        name: 'Right Wheelpath', line: { color: '#3b82f6', width: 2 },
        customdata: getStationLabels(rightTrace.x), 
        hovertemplate: `<b>Sta:</b> %{customdata}<br><b>IRI:</b> %{y:.1f} ${iriUnit}<extra></extra>`
      });
    }
    if (wheelpathVisibility.avg && avgTrace.x.length > 0) {
      data.push({
        ...commonLayout,
        x: avgTrace.x, y: avgTrace.y,
        name: 'Average', line: { color: '#9333ea', width: 2.5 },
        customdata: getStationLabels(avgTrace.x), 
        hovertemplate: `<b>Sta:</b> %{customdata}<br><b>Avg IRI:</b> %{y:.1f} ${iriUnit}<extra></extra>`
      });
    }
    if (thresholds?.active) {
      const thresholdValue = isStandard ? thresholds.active : thresholds.active * IN_PER_MI_TO_M_PER_KM;
      data.push({
        x: [stationRange.min, stationRange.max],
        y: [thresholdValue, thresholdValue],
        name: 'Threshold', type: 'scatter', mode: 'lines', 
        line: { color: '#ef4444', width: 2, dash: 'dash' },
        hoverinfo: 'skip'
      });
    }
    return data;
  }, [leftTrace, rightTrace, avgTrace, wheelpathVisibility, thresholds, isStandard, iriUnit, stationRange, alrTraces, formatStation]);

  const { tickVals, tickText } = useMemo(() => {
    if (!xAxisRange) return { tickVals: [], tickText: [] };
    const visibleRange = Math.abs(xAxisRange[1] - xAxisRange[0]);
    let tickInterval = 25;
    if (visibleRange > 50000) tickInterval = 5000;
    else if (visibleRange > 20000) tickInterval = 2000;
    else if (visibleRange > 10000) tickInterval = 1000;
    else if (visibleRange > 5000) tickInterval = 500;
    else if (visibleRange > 2000) tickInterval = 200;
    else if (visibleRange > 1000) tickInterval = 100;
    else if (visibleRange > 500) tickInterval = 50;

    const vals = [];
    const text = [];
    const startTick = Math.floor(Math.min(xAxisRange[0], xAxisRange[1]) / tickInterval) * tickInterval;
    const endTick = Math.ceil(Math.max(xAxisRange[0], xAxisRange[1]) / tickInterval) * tickInterval;

    // Generate ticks Ascending or Descending based on direction?
    // Plotly arrays ticks based on values. So min->max is fine.
    for (let s = startTick; s <= endTick; s += tickInterval) {
      vals.push(s);
      text.push(formatStation(s));
    }
    return { tickVals: vals, tickText: text };
  }, [xAxisRange, formatStation]);

  // ... (Shapes same) ...
  const { segmentShapes, segmentAnnotations } = useMemo(() => {
    if (!showSegments || !segments || segments.length === 0) return { segmentShapes: [], segmentAnnotations: [] };
    const uniqueStations = new Set();
    segments.forEach(seg => {
        if (seg.from_station_ft !== null) uniqueStations.add(seg.from_station_ft);
        if (seg.to_station_ft !== null) uniqueStations.add(seg.to_station_ft);
    });
    const sortedStations = Array.from(uniqueStations).sort((a, b) => a - b);
    const shapes = sortedStations.map(station => ({
      type: 'line', x0: station, x1: station, y0: 0, y1: 1, xref: 'x', yref: 'paper',
      line: { color: '#9CA3AF', width: 1, dash: 'dot' }, layer: 'below'
    }));
    const annotations = sortedStations.map(station => ({
      x: station, y: 1, xref: 'x', yref: 'paper', text: formatStation(station),
      showarrow: false, xanchor: 'left', yanchor: 'top', textangle: -90, font: { size: 10, color: '#6B7280' }
    }));
    return { segmentShapes: shapes, segmentAnnotations: annotations };
  }, [showSegments, segments, formatStation]);

  const highlightShape = useMemo(() => {
    if (!selectedSegment) return null;
    return {
      type: 'rect', xref: 'x', yref: 'paper', x0: selectedSegment.from_station_ft, x1: selectedSegment.to_station_ft,
      y0: 0, y1: 1, fillcolor: 'rgba(250, 204, 21, 0.4)', line: { width: 0 }, layer: 'below'
    };
  }, [selectedSegment]);

  const ghostShape = useMemo(() => {
    if (!hoveredStation) return null;
    return {
      type: 'line', xref: 'x', yref: 'paper', x0: hoveredStation, x1: hoveredStation,
      y0: 0, y1: 1, line: { color: 'rgba(59, 130, 246, 0.5)', width: 1, dash: 'solid' }, layer: 'above'
    };
  }, [hoveredStation]);

  const layout = useMemo(() => ({
    xaxis: {
      showgrid: true, gridcolor: '#e5e7eb',
      tickmode: 'array', tickvals: tickVals, ticktext: [],
      range: xAxisRange,
      autorange: false,
      showspikes: false,
      fixedrange: false,
      showticklabels: false,
    },
    yaxis: {
      title: { text: `IRI (${iriUnit})`, font: { size: 10 } },
      showgrid: true, gridcolor: '#e5e7eb',
      fixedrange: true,
      autorange: false,
      range: yAxisRange
    },
    showlegend: false,
    margin: { l: 50, r: 15, t: 0, b: 0 },
    hovermode: 'x',
    autosize: true,
    plot_bgcolor: '#fafafa', paper_bgcolor: '#ffffff',
    dragmode: 'zoom',
    uirevision: 'constant', 
    shapes: [
      ...(highlightShape ? [highlightShape] : []), 
      ...segmentShapes,
      {
        type: 'line', xref: 'x', yref: 'paper',
        x0: currentStation, x1: currentStation, y0: 0, y1: 1,
        line: { color: '#9333ea', width: 3 }, layer: 'above'
      },
    ],
    annotations: segmentAnnotations
  }), [xAxisRange, yAxisRange, tickVals, tickText, iriUnit, currentStation, segmentShapes, segmentAnnotations, highlightShape]);

  const config = useMemo(() => ({
    displayModeBar: true, displaylogo: false, responsive: true, scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d'],
  }), []);

  const handleRelayout = useCallback((eventData) => {
    if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
      setXAxisRange([eventData['xaxis.range[0]'], eventData['xaxis.range[1]']]);
    } else if (eventData['xaxis.autorange']) {
      // Respect direction on reset: If reversed, ensure [Max, Min]
      const start = isReversed ? stationRange.max : stationRange.min;
      const end = isReversed ? stationRange.min : stationRange.max;
      setXAxisRange([start, end]); 
    }
  }, [stationRange, setXAxisRange, isReversed]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (hoveredPointRef.current && hoveredPointRef.current.data && hoveredPointRef.current.data.meta && hoveredPointRef.current.data.meta.isAlr) {
        const alrId = hoveredPointRef.current.data.meta.id;
        setContextMenu({ x: e.clientX, y: e.clientY, alrId });
    }
  };

  const plotRef = useRef(null);

  const handlePlotClick = useCallback((e) => {
    const plotEl = plotRef.current?.el;
    if (!plotEl) return;
    const xaxis = plotEl._fullLayout?.xaxis;
    if (!xaxis || typeof xaxis.p2d !== 'function') return;
    const plotRect = plotEl.querySelector('.plotarea')?.getBoundingClientRect()
      || plotEl.querySelector('.draglayer')?.getBoundingClientRect();
    if (!plotRect) return;
    const px = e.clientX - plotRect.left;
    if (px < 0 || px > plotRect.width) return;
    const station = xaxis.p2d(px);
    if (station >= Math.min(stationRange.min, stationRange.max) && station <= Math.max(stationRange.min, stationRange.max)) {
      onChartClick({ points: [{ x: station }] });
    }
  }, [onChartClick, stationRange]);

  return (
    <div className="relative w-full h-full cursor-crosshair" onContextMenu={handleContextMenu} onClick={handlePlotClick}>
        <style>{` .js-plotly-plot .plotly .hoverlayer { display: none !important; } `}</style>

        <Plot
            ref={plotRef}
            data={plotData}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
            onRelayout={handleRelayout}
            onClick={onChartClick}
            onHover={(data) => {
                if (data.points && data.points.length > 0) {
                    hoveredPointRef.current = data.points[0];
                    onChartHoverRaw(data);
                }
            }}
            onUnhover={() => {
                hoveredPointRef.current = null;
                onChartHoverRaw(null);
            }}
        />

        {contextMenu && (
            <div 
                className="fixed z-[9999] bg-white border border-gray-200 shadow-lg rounded-md py-1 min-w-[140px] animate-in fade-in zoom-in-95 duration-100"
                style={{ top: contextMenu.y, left: contextMenu.x }}
            >
                <button
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                    onClick={() => {
                        onSelectAlr(contextMenu.alrId);
                        setContextMenu(null);
                    }}
                >
                    <MapPin className="w-4 h-4" />
                    Show on Map
                </button>
            </div>
        )}
    </div>
  );
});

// Optimized Elevation Chart
const FullElevationChart = React.memo(({ 
  elevationProfileData, 
  currentStation, 
  stationRange, 
  units,
  wheelpathVisibility,
  ignoredSegments,
  surfaceEvents = [],
  formatStation,
  xAxisRange, 
  setXAxisRange,
  segments,
  showSegments,
  selectedSegment,
  hoveredStation, 
  onChartHoverRaw,
  onChartClick,
  cutoffWavelength,
  isReversed // Received Prop
}) => {
  
  const isStandard = units === 'standard';
  // Worker emits elevation in inches.  Standard view shows inches as-is;
  // metric view converts to meters.
  const elevFactor = isStandard ? 1.0 : INCHES_TO_METERS;
  const elevUnit = isStandard ? 'in' : 'm';

  // --- 1. Calculate Smoothed Data ---
  const { smoothedLeftData, smoothedRightData } = useMemo(() => {
    let leftData = elevationProfileData.leftTexture;
    let rightData = elevationProfileData.rightTexture;

    if (!leftData && elevationProfileData.leftProfile) {
        leftData = applyHighPassFilter(elevationProfileData.leftProfile, cutoffWavelength);
    }
    if (!rightData && elevationProfileData.rightProfile) {
        rightData = applyHighPassFilter(elevationProfileData.rightProfile, cutoffWavelength);
    }

    const validLeft = leftData || elevationProfileData.leftProfile;
    const validRight = rightData || elevationProfileData.rightProfile;
    const valueKey = (validLeft && validLeft.length > 0 && validLeft[0].texture !== undefined) ? 'texture' : 'elevation';

    const smoothedLeft = applyMovingAverage(validLeft, valueKey, 5);
    const smoothedRight = applyMovingAverage(validRight, valueKey, 5);

    return {
      smoothedLeftData: smoothedLeft,
      smoothedRightData: smoothedRight
    };
  }, [elevationProfileData, cutoffWavelength]);

  // --- 2. Create Traces for Main Lines ---
  const { leftTrace, rightTrace } = useMemo(() => {
    const valueKey = (smoothedLeftData && smoothedLeftData.length > 0 && smoothedLeftData[0].texture !== undefined) ? 'texture' : 'elevation';
    return {
      leftTrace: prepareDataWithGaps(smoothedLeftData, valueKey, elevFactor, ignoredSegments),
      rightTrace: prepareDataWithGaps(smoothedRightData, valueKey, elevFactor, ignoredSegments),
    };
  }, [smoothedLeftData, smoothedRightData, elevFactor, ignoredSegments]);

  const yAxisRange = useMemo(() => {
    const arraysToCheck = [];
    if (wheelpathVisibility.left) arraysToCheck.push(leftTrace);
    if (wheelpathVisibility.right) arraysToCheck.push(rightTrace);
    const currentXMin = xAxisRange ? xAxisRange[0] : stationRange.min;
    const currentXMax = xAxisRange ? xAxisRange[1] : stationRange.max;
    
    // Updated Helper handles reverse ranges
    const stats = getVisibleYStats(arraysToCheck, currentXMin, currentXMax);
    
    if (!stats) return [-1, 1];
    const { min, max } = stats;
    const range = max - min;
    const padding = range === 0 ? 0.2 : range * 0.1;
    return [min - padding, max + padding];
  }, [leftTrace, rightTrace, wheelpathVisibility, xAxisRange, stationRange]);

  // ... (Event Traces Logic unchanged) ...
  const { eventTraces } = useMemo(() => {
    if (!surfaceEvents || surfaceEvents.length === 0) return { eventTraces: [] };
    
    const bumps = { x: [], y: [], text: [], customdata: [] };
    const dips = { x: [], y: [], text: [], customdata: [] };
    const bumpFills = { x: [], y: [] };
    const dipFills = { x: [], y: [] };
    const chordLines = { x: [], y: [] };

    const leftProfile = smoothedLeftData;
    const rightProfile = smoothedRightData;

    surfaceEvents.forEach(event => {
      if (event.wheelpath === 'Left' && !wheelpathVisibility.left) return;
      if (event.wheelpath === 'Right' && !wheelpathVisibility.right) return;
      const isBump = event.eventType === 'Bump';
      const isLeft = event.wheelpath === 'Left';
      const profile = isLeft ? leftProfile : rightProfile;
      if (!profile) return;
      const valueKey = (profile[0] && profile[0].texture !== undefined) ? 'texture' : 'elevation';
      const start = event.distance;
      const end = event.endStation || event.distance + 5;
      const sIdx = findClosestIndex(profile, start);
      const eIdx = findClosestIndex(profile, end);
      if (sIdx !== -1 && eIdx !== -1) {
          const startPoint = profile[sIdx];
          const endPoint = profile[eIdx];
          chordLines.x.push(startPoint.station);
          chordLines.y.push(startPoint[valueKey] * elevFactor);
          chordLines.x.push(endPoint.station);
          chordLines.y.push(endPoint[valueKey] * elevFactor);
          chordLines.x.push(null);
          chordLines.y.push(null);
          const targetFill = isBump ? bumpFills : dipFills;
          for (let k = sIdx; k <= eIdx; k++) {
             targetFill.x.push(profile[k].station);
             targetFill.y.push(profile[k][valueKey] * elevFactor);
          }
          targetFill.x.push(startPoint.station);
          targetFill.y.push(startPoint[valueKey] * elevFactor);
          targetFill.x.push(null);
          targetFill.y.push(null);
          const searchStation = event.peak_station || (start + (end - start) / 2);
          const pIdx = findClosestIndex(profile, searchStation);
          const peakPoint = profile[pIdx];
          if (peakPoint) {
            const yVal = peakPoint[valueKey] * elevFactor;
            const targetObj = isBump ? bumps : dips;
            targetObj.x.push(peakPoint.station);
            targetObj.y.push(yVal);
            targetObj.text.push(`${event.eventType} @ ${formatStation(start)}`);
            targetObj.customdata.push({
              type: event.eventType,
              mag: event.height || event.magnitude || 'N/A',
              wp: event.wheelpath,
              id: event.id,
              unit: elevUnit
            });
          }
      }
    });
    const traces = [];
    if (bumpFills.x.length > 0) traces.push({ x: bumpFills.x, y: bumpFills.y, mode: 'lines', type: 'scatter', fill: 'toself', fillcolor: 'rgba(239, 68, 68, 0.2)', line: { width: 0 }, hoverinfo: 'skip', showlegend: false });
    if (dipFills.x.length > 0) traces.push({ x: dipFills.x, y: dipFills.y, mode: 'lines', type: 'scatter', fill: 'toself', fillcolor: 'rgba(59, 130, 246, 0.2)', line: { width: 0 }, hoverinfo: 'skip', showlegend: false });
    if (chordLines.x.length > 0) traces.push({ x: chordLines.x, y: chordLines.y, mode: 'lines', type: 'scatter', name: 'Event Chords', line: { color: 'rgba(50, 50, 50, 0.8)', width: 1.5 }, hoverinfo: 'skip', showlegend: false });
    if (bumps.x.length > 0) traces.push({ x: bumps.x, y: bumps.y, mode: 'markers+text', text: bumps.text, textposition: 'top center', textfont: { family: 'sans-serif', size: 10, color: '#ef4444' }, type: 'scatter', name: 'Bumps', marker: { color: '#ef4444', size: 8, symbol: 'circle', line: { color: 'white', width: 1 } }, customdata: bumps.customdata, hovertemplate: '<b>%{customdata.type}</b><br>Mag: %{customdata.mag} %{customdata.unit}<extra></extra>', showlegend: false });
    if (dips.x.length > 0) traces.push({ x: dips.x, y: dips.y, mode: 'markers+text', text: dips.text, textposition: 'bottom center', textfont: { family: 'sans-serif', size: 10, color: '#3b82f6' }, type: 'scatter', name: 'Dips', marker: { color: '#3b82f6', size: 8, symbol: 'circle', line: { color: 'white', width: 1 } }, customdata: dips.customdata, hovertemplate: '<b>%{customdata.type}</b><br>Mag: %{customdata.mag} %{customdata.unit}<extra></extra>', showlegend: false });
    return { eventTraces: traces };
  }, [surfaceEvents, wheelpathVisibility, smoothedLeftData, smoothedRightData, elevFactor, elevUnit, formatStation]);

  const plotData = useMemo(() => {
    const data = [];
    if (eventTraces) {
        const fills = eventTraces.filter(t => t.fill === 'toself');
        data.push(...fills);
    }
    const getStationLabels = (xArr) => xArr.map(x => x !== null ? formatStation(x) : '');
    const commonLayout = { type: 'scatter', mode: 'lines', hoverinfo: 'y+text' };

    if (wheelpathVisibility.left && leftTrace.x.length > 0) {
      data.push({ ...commonLayout, x: leftTrace.x, y: leftTrace.y, name: 'Left Wheelpath', line: { color: '#ef4444', width: 2 }, customdata: getStationLabels(leftTrace.x), hovertemplate: `<b>Sta:</b> %{customdata}<br><b>Elev:</b> %{y:.2f} ${elevUnit}<extra></extra>` });
    }
    if (wheelpathVisibility.right && rightTrace.x.length > 0) {
      data.push({ ...commonLayout, x: rightTrace.x, y: rightTrace.y, name: 'Right Wheelpath', line: { color: '#f97316', width: 2 }, customdata: getStationLabels(rightTrace.x), hovertemplate: `<b>Sta:</b> %{customdata}<br><b>Elev:</b> %{y:.2f} ${elevUnit}<extra></extra>` });
    }
    if (eventTraces) {
        const markers = eventTraces.filter(t => t.fill !== 'toself');
        data.push(...markers);
    }
    return data;
  }, [leftTrace, rightTrace, wheelpathVisibility, elevUnit, eventTraces, formatStation]);

  const { tickVals, tickText } = useMemo(() => {
    if (!xAxisRange) return { tickVals: [], tickText: [] };
    const visibleRange = Math.abs(xAxisRange[1] - xAxisRange[0]);
    let tickInterval = 25;
    if (visibleRange > 50000) tickInterval = 5000;
    else if (visibleRange > 20000) tickInterval = 2000;
    else if (visibleRange > 10000) tickInterval = 1000;
    else if (visibleRange > 5000) tickInterval = 500;
    else if (visibleRange > 2000) tickInterval = 200;
    else if (visibleRange > 1000) tickInterval = 100;
    else if (visibleRange > 500) tickInterval = 50;
    
    const vals = [];
    const text = [];
    const startTick = Math.floor(Math.min(xAxisRange[0], xAxisRange[1]) / tickInterval) * tickInterval;
    const endTick = Math.ceil(Math.max(xAxisRange[0], xAxisRange[1]) / tickInterval) * tickInterval;
    
    for (let s = startTick; s <= endTick; s += tickInterval) {
      vals.push(s);
      text.push(formatStation(s));
    }
    return { tickVals: vals, tickText: text };
  }, [xAxisRange, formatStation]);

  const highlightShape = useMemo(() => {
    if (!selectedSegment) return null;
    return {
      type: 'rect', xref: 'x', yref: 'paper', x0: selectedSegment.from_station_ft, x1: selectedSegment.to_station_ft,
      y0: 0, y1: 1, fillcolor: 'rgba(250, 204, 21, 0.4)', line: { width: 0 }, layer: 'below'
    };
  }, [selectedSegment]);

  const ghostShape = useMemo(() => {
    if (!hoveredStation) return null;
    return {
      type: 'line', xref: 'x', yref: 'paper', x0: hoveredStation, x1: hoveredStation,
      y0: 0, y1: 1, line: { color: 'rgba(59, 130, 246, 0.5)', width: 1, dash: 'solid' }, layer: 'above'
    };
  }, [hoveredStation]);

  const { segmentShapes, segmentAnnotations } = useMemo(() => {
    if (!showSegments || !segments || segments.length === 0) return { segmentShapes: [], segmentAnnotations: [] };
    const uniqueStations = new Set();
    segments.forEach(seg => {
        if (seg.from_station_ft !== null) uniqueStations.add(seg.from_station_ft);
        if (seg.to_station_ft !== null) uniqueStations.add(seg.to_station_ft);
    });
    const sortedStations = Array.from(uniqueStations).sort((a, b) => a - b);
    const shapes = sortedStations.map(station => ({
      type: 'line', x0: station, x1: station, y0: 0, y1: 1, xref: 'x', yref: 'paper',
      line: { color: '#9CA3AF', width: 1, dash: 'dot' }, layer: 'below'
    }));
    const annotations = sortedStations.map(station => ({
      x: station, y: 1, xref: 'x', yref: 'paper', text: formatStation(station),
      showarrow: false, xanchor: 'left', yanchor: 'top', textangle: -90, font: { size: 10, color: '#6B7280' }
    }));
    return { segmentShapes: shapes, segmentAnnotations: annotations };
  }, [showSegments, segments, formatStation]);

  const layout = useMemo(() => ({
    xaxis: {
      title: { text: 'Station', font: { size: 9, color: '#9ca3af' } },
      showgrid: true, gridcolor: '#e5e7eb',
      tickmode: 'array', tickvals: tickVals, ticktext: tickText, tickangle: -60,
      tickfont: { size: 8 },
      range: xAxisRange,
      autorange: false,
      showspikes: false,
      fixedrange: false,
    },
    yaxis: {
      title: { text: `Deviation (${elevUnit})`, font: { size: 10 } },
      showgrid: true, gridcolor: '#e5e7eb',
      fixedrange: true,
      autorange: false,
      range: yAxisRange
    },
    showlegend: false,
    margin: { l: 50, r: 15, t: 0, b: 50 },
    hovermode: 'x',
    autosize: true,
    plot_bgcolor: '#fafafa', paper_bgcolor: '#ffffff',
    dragmode: 'zoom',
    uirevision: 'constant', 
    shapes: [
      ...(highlightShape ? [highlightShape] : []), 
      ...segmentShapes,
      {
        type: 'line', xref: 'x', yref: 'paper',
        x0: currentStation, x1: currentStation, y0: 0, y1: 1,
        line: { color: '#9333ea', width: 3 }, layer: 'above'
      },
    ],
    annotations: segmentAnnotations
  }), [xAxisRange, yAxisRange, tickVals, tickText, elevUnit, currentStation, highlightShape, segmentShapes, segmentAnnotations]);

  const config = useMemo(() => ({
    displayModeBar: true, displaylogo: false, responsive: true, scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d'],
  }), []);

  const handleRelayout = useCallback((eventData) => {
    if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
      setXAxisRange([eventData['xaxis.range[0]'], eventData['xaxis.range[1]']]);
    } else if (eventData['xaxis.autorange']) {
      // Respect direction on reset
      const start = isReversed ? stationRange.max : stationRange.min;
      const end = isReversed ? stationRange.min : stationRange.max;
      setXAxisRange([start, end]); 
    }
  }, [stationRange, setXAxisRange, isReversed]);

  const plotRef = useRef(null);

  const handlePlotClick = useCallback((e) => {
    const plotEl = plotRef.current?.el;
    if (!plotEl) return;
    const xaxis = plotEl._fullLayout?.xaxis;
    if (!xaxis || typeof xaxis.p2d !== 'function') return;
    const plotRect = plotEl.querySelector('.plotarea')?.getBoundingClientRect()
      || plotEl.querySelector('.draglayer')?.getBoundingClientRect();
    if (!plotRect) return;
    const px = e.clientX - plotRect.left;
    if (px < 0 || px > plotRect.width) return;
    const station = xaxis.p2d(px);
    if (station >= Math.min(stationRange.min, stationRange.max) && station <= Math.max(stationRange.min, stationRange.max)) {
      onChartClick({ points: [{ x: station }] });
    }
  }, [onChartClick, stationRange]);

  return (
    <div className="relative w-full h-full cursor-crosshair" onClick={handlePlotClick}>
        <style>{` .js-plotly-plot .plotly .hoverlayer { display: none !important; } `}</style>

        <Plot
            ref={plotRef}
            data={plotData}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
            onRelayout={handleRelayout}
            onClick={onChartClick}
            onHover={(data) => {
                if (data.points && data.points.length > 0) {
                    onChartHoverRaw(data);
                }
            }}
            onUnhover={() => { onChartHoverRaw(null); }}
        />
    </div>
  );
});

export default KmlChartsDrawer;